import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { RowClassName } from '../../../../enums';
import Badge from '../../../../components/Badge';
import Button from '../../../../components/Button';
import Link from '../../../../components/Link';
import { TableContext } from '../../../../contexts/TableContext';
import { AuthContext } from '../../../../contexts/AuthContext';
import ElipsisCell from '../../../../components/TableBox/Cells/EllipsisCell';
import ElipsisText from '../../../../components/ElipsisText';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { MonthlyStockResponse } from '../../../../interfaces/monthlyStock.interface';
import {
  Modules,
  PermissionModulesEnum,
  PermissionActionsEnum,
} from '../../../../enums';
import { hasResourseAccess } from '../../../../utils/methods';

export interface RowProps {
  item: MonthlyStockResponse;
  className?: RowClassName;
}

const Row: React.FC<RowProps> = ({ item, className }) => {
  const navigate = useNavigate();
  const { headCells } = useContext(TableContext);
  const { isSystemAdmin, permissions } = useContext(AuthContext);

  const hasViewAccess = hasResourseAccess(isSystemAdmin, permissions, {
    accessibleTo: [{ [PermissionModulesEnum.MonthlyStock]: [PermissionActionsEnum.View] }],
  });

  const getStatusBadgeClass = (statusCode: string) => {
    switch (statusCode?.toLowerCase()) {
      case 'approved': return 'success';
      case 'rejected': return 'danger';
      case 'processing': return 'info';
      case 'draft':
      default: return 'warning';
    }
  };

  const isViewOnly = ['approved', 'rejected'].includes(item.status?.code?.toLowerCase());

  const handleActionClick = () => {
    const mode = isViewOnly ? 'view' : 'edit';
    navigate(`${Modules.monthlyStock}/verify-stock/${mode}/${item.id}`);
  };

  return (
    <tr className={className}>
      {headCells.map((headCell) => {
        if (headCell.id === VALIDATION_FIELD_MAPPING.month) {
          const monthYear = item.month ? `${item.month.name} ${item.year}` : '-';
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <ElipsisText
                  width="100%"
                  text={monthYear}
                  node={<Link text={monthYear} />}
                  className="h6 mb-1 text-decoration-underline hyperlink"
                  onClick={handleActionClick}
                />
              }
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.stockist) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.stockist?.name || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.branch) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.branch?.name || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.template) {
          return <ElipsisCell key={headCell.id} width={headCell.width} text={item.template?.name || '-'} />;
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.uploadedBy) {
          return (
            <ElipsisCell
              key={headCell.id}
              width={headCell.width}
              node={
                <div>
                  <div className="fw-medium">{item.uploaded_by?.name || '-'}</div>
                  <small className="text-muted">
                    {item.uploaded_by?.time ? new Date(item.uploaded_by.time).toLocaleDateString() : ''}
                  </small>
                </div>
              }
            />
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.status) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <Badge
                text={item.status?.name || '-'}
                variant={getStatusBadgeClass(item.status?.code)}
              />
            </td>
          );
        }

        if (headCell.id === VALIDATION_FIELD_MAPPING.actions) {
          return (
            <td key={headCell.id} width={headCell.width}>
              <div className="d-flex align-items-center">
                {hasViewAccess && (
                  <Button
                    className="btn btn-sm btn-icon btn-text-secondary waves-effect waves-light rounded-pill me-1"
                    icon={<i className={`ri-${isViewOnly ? 'eye-line' : 'edit-box-line'} ri-20px`}></i>}
                    onClick={handleActionClick}
                  />
                )}
                <Button
                  className="btn btn-sm btn-icon btn-text-secondary rounded-pill waves-effect waves-light"
                  icon={<i className="ri-history-line ri-20px"></i>}
                />
              </div>
            </td>
          );
        }

        const value = item[headCell.id as keyof MonthlyStockResponse];
        return <ElipsisCell key={headCell.id} width={headCell.width} text={value ? String(value) : '-'} />;
      })}
    </tr>
  );
};

export default Row;
